// src/components/PainelEvidencias.jsx
// =============================================================================
// PAINEL DE EVIDÊNCIAS  ·  aba do GestaoDiarioBordo (Gestão de Frota)
// -----------------------------------------------------------------------------
// Renderiza, DENTRO do painel web, as fotos de evidência que o motorista sobe
// pela guia "Evidências" do app (ColetaEvidencias.js) — modo livre ou vistoria
// de troca de veículo (handover). As imagens vivem no Drive (privadas) e são
// carregadas aqui via blob autenticado (/api/anexos/drive/:fileId), o mesmo
// padrão usado na revisão de CNH — um <img src> direto não funcionaria porque
// o endpoint exige o header Authorization, que só o axios (instância `api`)
// consegue anexar.
//
// Filtro de data (dois modos):
//   • Dia rápido   → ‹ [data] ›  +  botão "Hoje"
//   • Intervalo    → de [data] até [data]
//
// As fotos são agrupadas por DIA de lançamento (criado_em). Filtros extras de
// tipo (livre/handover) e categoria. Clique no card abre o lightbox.
//
// Contrato de dados:
//   GET /api/diario-bordo/evidencias?de=YYYY-MM-DD&ate=YYYY-MM-DD&tipo=&categoria=
//     → [{ id, motorista_id, motorista_nome, veiculo_id, veiculo_anterior_id,
//          placa, categoria, tipo, drive_file_id, comparacao_status,
//          comparacao_resumo, comparacao_confianca, dia, criado_em }]
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaSyncAlt, FaCamera, FaChevronLeft, FaChevronRight, FaTimes, FaCalendarDay,
  FaCalendarAlt, FaImage, FaExclamationTriangle, FaCheckCircle, FaClock,
  FaCarSide, FaExchangeAlt, FaUser, FaExternalLinkAlt,
} from 'react-icons/fa';
import './PainelEvidencias.css';

// ── Taxonomia (espelho do app ColetaEvidencias.js) ──────────────────────────
const CAT_LABELS = {
  pneus: 'Pneus', pintura: 'Pintura', velocimetro: 'Velocímetro', lataria: 'Lataria',
  parabrisa: 'Para-brisa', farois: 'Faróis', interior: 'Interior', motor: 'Motor',
  outros: 'Outros',
};
const CAT_ORDER = Object.keys(CAT_LABELS);
const labelCat = (k) => CAT_LABELS[k] || k || 'Outros';

// Status da comparação por IA (handover)
const CMP_CFG = {
  divergencia_detectada: { cor: '#ef4444', bg: '#fee2e2', label: 'Divergência', icon: FaExclamationTriangle },
  sem_divergencia:       { cor: '#16a34a', bg: '#dcfce7', label: 'Sem divergência', icon: FaCheckCircle },
  pendente:              { cor: '#f59e0b', bg: '#fef3c7', label: 'Comparando…', icon: FaClock },
  falha:                 { cor: '#64748b', bg: '#f1f5f9', label: 'Falha na análise', icon: FaTimes },
  nao_aplicavel:         null,
};

// ── Helpers de data ──────────────────────────────────────────────────────────
const hojeISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
const addDias = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
const fmtDiaExtenso = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  const s = d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtHora = (criadoEm) => (criadoEm ? criadoEm.slice(11, 16) : '');

// ── Carrega uma imagem do Drive via blob autenticado ────────────────────────
async function fetchDriveBlob(fileId) {
  if (!fileId) return null;
  const resp = await api.get(`/api/anexos/drive/${fileId}`, { responseType: 'blob' });
  return URL.createObjectURL(resp.data);
}

// ── Thumbnail (cada card cuida do seu próprio ciclo de blob) ─────────────────
function EvidenciaThumb({ fileId, alt, onClick }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    let criada = null;
    (async () => {
      try {
        const u = await fetchDriveBlob(fileId);
        if (!vivo) { if (u) URL.revokeObjectURL(u); return; }
        criada = u;
        setUrl(u);
      } catch {
        if (vivo) setErro(true);
      }
    })();
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada); };
  }, [fileId]);

  if (erro) {
    return <div className="ev-thumb ev-thumb--erro"><FaImage /></div>;
  }
  if (!url) {
    return <div className="ev-thumb ev-thumb--loading"><FaSyncAlt className="spin" /></div>;
  }
  return (
    <button type="button" className="ev-thumb" onClick={onClick} title="Ampliar">
      <img src={url} alt={alt} loading="lazy" />
      <span className="ev-thumb-zoom"><FaExternalLinkAlt /></span>
    </button>
  );
}

// ── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ ev, onClose }) {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    let criada = null;
    (async () => {
      try {
        const u = await fetchDriveBlob(ev.drive_file_id);
        if (!vivo) { if (u) URL.revokeObjectURL(u); return; }
        criada = u;
        setUrl(u);
      } catch {
        if (vivo) setErro(true);
      }
    })();
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada); };
  }, [ev.drive_file_id]);

  const cmp = CMP_CFG[ev.comparacao_status];

  return (
    <div className="ev-lb-backdrop" onClick={onClose}>
      <div className="ev-lb" onClick={(e) => e.stopPropagation()}>
        <button className="ev-lb-close" onClick={onClose}><FaTimes /></button>
        <div className="ev-lb-img">
          {erro ? <div className="ev-lb-erro"><FaImage /> Não foi possível carregar a imagem.</div>
            : !url ? <div className="ev-lb-loading"><FaSyncAlt className="spin" /></div>
              : <img src={url} alt={`${labelCat(ev.categoria)} — ${ev.motorista_nome}`} />}
        </div>
        <div className="ev-lb-info">
          <div className="ev-lb-info-main">
            <span className={`ev-cat-chip ev-cat--${ev.categoria}`}>{labelCat(ev.categoria)}</span>
            <span className={`ev-tipo-badge ev-tipo--${ev.tipo}`}>
              {ev.tipo === 'handover' ? <><FaExchangeAlt /> Troca de veículo</> : <><FaCamera /> Livre</>}
            </span>
          </div>
          <div className="ev-lb-meta">
            <span><FaUser /> {ev.motorista_nome}</span>
            {ev.placa && <span><FaCarSide /> {ev.placa}</span>}
            <span><FaClock /> {ev.criado_em}</span>
          </div>
          {cmp && (
            <div className="ev-lb-cmp" style={{ background: cmp.bg, color: cmp.cor }}>
              <cmp.icon />
              <div>
                <strong>{cmp.label}{ev.comparacao_confianca != null ? ` · ${Math.round(ev.comparacao_confianca * 100)}%` : ''}</strong>
                {ev.comparacao_resumo && <p>{ev.comparacao_resumo}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function PainelEvidencias() {
  const [modoIntervalo, setModoIntervalo] = useState(false);
  const [dia, setDia] = useState(hojeISO());
  const [de, setDe] = useState(hojeISO());
  const [ate, setAte] = useState(hojeISO());

  const [tipo, setTipo] = useState('');       // '' | 'livre' | 'handover'
  const [categoria, setCategoria] = useState(''); // '' | chave da categoria

  const [loading, setLoading] = useState(true);
  const [evidencias, setEvidencias] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  // intervalo efetivo enviado à API (dia rápido = mesma data nas duas pontas)
  const range = useMemo(
    () => (modoIntervalo ? { de, ate } : { de: dia, ate: dia }),
    [modoIntervalo, dia, de, ate]
  );

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { de: range.de, ate: range.ate };
      if (tipo) params.tipo = tipo;
      if (categoria) params.categoria = categoria;
      const { data } = await api.get('/api/diario-bordo/evidencias', { params });
      setEvidencias(data || []);
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar as evidências.');
    } finally {
      setLoading(false);
    }
  }, [range.de, range.ate, tipo, categoria]);

  useEffect(() => { carregar(); }, [carregar]);

  // Agrupa por dia (a lista já vem ordenada por criado_em DESC)
  const grupos = useMemo(() => {
    const map = new Map();
    evidencias.forEach((ev) => {
      const k = ev.dia || (ev.criado_em || '').slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    });
    return [...map.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([d, itens]) => ({ dia: d, itens }));
  }, [evidencias]);

  const resumo = useMemo(() => {
    const motoristas = new Set(evidencias.map((e) => e.motorista_id));
    const handover = evidencias.filter((e) => e.tipo === 'handover').length;
    const divergencias = evidencias.filter((e) => e.comparacao_status === 'divergencia_detectada').length;
    return { total: evidencias.length, motoristas: motoristas.size, handover, divergencias };
  }, [evidencias]);

  const rangeLabel = modoIntervalo
    ? `${fmtDiaExtenso(de)} → ${fmtDiaExtenso(ate)}`
    : fmtDiaExtenso(dia);

  return (
    <div className="ev-wrap">
      {/* ── Barra de filtros ── */}
      <div className="ev-filtros">
        <div className="ev-modo-toggle" role="tablist">
          <button
            className={!modoIntervalo ? 'is-active' : ''}
            onClick={() => setModoIntervalo(false)}
            type="button"
          >
            <FaCalendarDay /> Dia
          </button>
          <button
            className={modoIntervalo ? 'is-active' : ''}
            onClick={() => setModoIntervalo(true)}
            type="button"
          >
            <FaCalendarAlt /> Intervalo
          </button>
        </div>

        {!modoIntervalo ? (
          <div className="ev-dia-nav">
            <button className="ev-nav-btn" onClick={() => setDia((d) => addDias(d, -1))} title="Dia anterior">
              <FaChevronLeft />
            </button>
            <input type="date" value={dia} max={hojeISO()} onChange={(e) => setDia(e.target.value || hojeISO())} />
            <button
              className="ev-nav-btn"
              onClick={() => setDia((d) => addDias(d, 1))}
              disabled={dia >= hojeISO()}
              title="Próximo dia"
            >
              <FaChevronRight />
            </button>
            <button className="ev-hoje-btn" onClick={() => setDia(hojeISO())} disabled={dia === hojeISO()}>
              Hoje
            </button>
          </div>
        ) : (
          <div className="ev-intervalo">
            <label>De <input type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} /></label>
            <label>Até <input type="date" value={ate} min={de} max={hojeISO()} onChange={(e) => setAte(e.target.value)} /></label>
          </div>
        )}

        <div className="ev-filtros-extra">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos os tipos</option>
            <option value="livre">Livre</option>
            <option value="handover">Troca de veículo</option>
          </select>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Todas as categorias</option>
            {CAT_ORDER.map((k) => <option key={k} value={k}>{labelCat(k)}</option>)}
          </select>
          <button className="ev-nav-btn" onClick={carregar} disabled={loading} title="Recarregar">
            <FaSyncAlt className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Resumo ── */}
      <div className="ev-resumo">
        <div className="ev-resumo-titulo"><FaCamera /> {rangeLabel}</div>
        <div className="ev-resumo-chips">
          <span><b>{resumo.total}</b> foto(s)</span>
          <span><b>{resumo.motoristas}</b> motorista(s)</span>
          <span><b>{resumo.handover}</b> em troca de veículo</span>
          {resumo.divergencias > 0 && (
            <span className="ev-chip-alerta"><FaExclamationTriangle /> {resumo.divergencias} divergência(s)</span>
          )}
        </div>
      </div>

      {/* ── Conteúdo ── */}
      {loading ? (
        <div className="ev-loading"><FaSyncAlt className="spin" /> Carregando evidências…</div>
      ) : grupos.length === 0 ? (
        <div className="ev-empty">
          <FaImage />
          <p>Nenhuma evidência {modoIntervalo ? 'neste intervalo' : 'neste dia'}.</p>
          <span>Ajuste o período ou os filtros acima.</span>
        </div>
      ) : (
        grupos.map((g) => (
          <section key={g.dia} className="ev-grupo">
            <header className="ev-grupo-head">
              <h4>{fmtDiaExtenso(g.dia)}</h4>
              <span>{g.itens.length} foto(s)</span>
            </header>
            <div className="ev-grid">
              {g.itens.map((ev) => {
                const cmp = CMP_CFG[ev.comparacao_status];
                return (
                  <div key={ev.id} className="ev-card">
                    <div className="ev-card-media">
                      <EvidenciaThumb
                        fileId={ev.drive_file_id}
                        alt={`${labelCat(ev.categoria)} — ${ev.motorista_nome}`}
                        onClick={() => setLightbox(ev)}
                      />
                      <span className={`ev-card-cat ev-cat--${ev.categoria}`}>{labelCat(ev.categoria)}</span>
                      {ev.tipo === 'handover' && (
                        <span className="ev-card-tipo" title="Vistoria de troca de veículo"><FaExchangeAlt /></span>
                      )}
                      {cmp && ev.comparacao_status === 'divergencia_detectada' && (
                        <span className="ev-card-alerta" title={ev.comparacao_resumo || 'Divergência detectada'}>
                          <FaExclamationTriangle />
                        </span>
                      )}
                    </div>
                    <div className="ev-card-info">
                      <strong title={ev.motorista_nome}>{ev.motorista_nome}</strong>
                      <div className="ev-card-sub">
                        {ev.placa && <span><FaCarSide /> {ev.placa}</span>}
                        <span><FaClock /> {fmtHora(ev.criado_em)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {lightbox && <Lightbox ev={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}